<script setup>
import { inject, onMounted } from 'vue';
import { useMapMarker, useMapProjection, useVectorLayers } from '@/app/composables';
import { Map, View } from 'ol';
import { Tile as TileLayer } from 'ol/layer';
import { OSM } from 'ol/source';
import { defaults as controlsDefaults } from 'ol/control';
import { defaults as interactionDefaults } from 'ol/interaction';

let map = null;

const emit = defineEmits(['ctaClick']);

const scotlandCenterCoords = [266866.0, 771615.51];
const config = inject('config');
const proj27700 = useMapProjection();
const { vectorLayers } = useVectorLayers(config.vectorLayers);
const markerLayer = useMapMarker();

function findExtent() {
  const layer = vectorLayers.find((vl) => vl.getSource().getFeatures().length === 1);
  return layer.getSource().getExtent();
}

function instantiateMap() {
  return new Map({
    controls: controlsDefaults({
      zoom: false,
    }),
    interactions: interactionDefaults({
      doubleClickZoom: false,
      dragAndDrop: false,
      dragPan: false,
      keyboardPan: false,
      keyboardZoom: false,
      mouseWheelZoom: false,
      pointer: false,
      select: false,
    }),
    layers: [
      new TileLayer({
        source: new OSM(),
      }),
      ...vectorLayers,
      markerLayer,
    ],
    target: 'mapCanvas',
    view: new View({
      projection: proj27700,
      center: scotlandCenterCoords,
      maxZoom: 10,
      zoom: 3,
      extent: proj27700.getExtent(),
    }),
  });
}

function ctaClickHandler(event) {
  emit('ctaClick', event);
}

onMounted(() => {
  // Assign map instance
  map = instantiateMap();

  map.once('loadend', () => {
    map.getView().fit(findExtent());
  });
});
</script>

<template>
  <div class="c-map-simple">
    <div class="c-map-simple__action">
      <a
        @click="ctaClickHandler"
        :href="config.viewProjectMapCta.url"
        class="c-button c-button--form"
      >
        {{ config.viewProjectMapCta.label }}
      </a>
    </div>
    <div
      class="c-map-simple__canvas"
      id="mapCanvas"
      ref="mapCanvas"
      role="img"
      aria-label="Thumbnail map of the project area"
    />
  </div>
</template>

<style lang="scss">
@import '@/ui/styles/functions/functions';
@import '@/ui/styles/settings/units';
@import 'ol/ol.css';

.c-map-simple {
  position: relative;
  height: 100%;
  width: 100%;
}

.c-map-simple__action {
  position: absolute;
  right: $spacing-medium;
  bottom: $spacing-x-large;
  z-index: 1;
}

.c-map-simple__canvas {
  height: 100%;
  width: 100%;
}
</style>
